import {
    ProductCategorySearchBuilder,
    ProductCategorySearchResponse,
    ProductSearchBuilder,
    ProductSearchResponse,
    SearchCollectionBuilder,
    Searcher,
    SearchTermPredictionBuilder,
    SearchTermPredictionResponse
} from '@relewise/client';
import { RelewiseCategoryResult, RelewiseProductResult, RelewiseSearchTermPredictionResult } from '../types/relewiseData';
import { RELEWISE_MINIMUM_STOCK_COUNT } from '../const';
import { IRequestContext } from '@msdyn365-commerce/core-internal';
import { RelewiseMappingService } from './MappingService';
import { RelewiseUserService } from './UserService';

const NUMBER_OF_VARIANTS_PER_PRODUCT_IN_AUTOSUGGEST = 2;

export interface AutosuggestSearchParams {
    query: string;
    shouldFetchProducts: boolean;
    shouldFetchCategories: boolean;
    shouldFetchSearchTermPredictions: boolean;
    numberOfProductResults: number;
    numberOfCategoryResults: number;
    numberOfKeywordResults: number;
}
export interface AutosuggestSearchResult {
    products: RelewiseProductResult[] | undefined;
    categories: RelewiseCategoryResult[] | undefined;
    searchTermPredictions: RelewiseSearchTermPredictionResult[] | undefined;
}

interface InitializedService {
    searcher: Searcher;
    currencyId: string;
    disabled: false;
    locale: string;
    legalEntityId: string;
    environmentName: string;
    relewiseMappingService: RelewiseMappingService;
    relewiseUserService: RelewiseUserService;
}

export class RelewiseSearchService {
    protected searcher: Searcher | undefined;
    protected currencyId: string | undefined;
    protected locale: string | undefined;
    protected legalEntityId: string | undefined;
    protected environmentName: string | undefined;
    protected relewiseMappingService: RelewiseMappingService | undefined;
    protected relewiseUserService: RelewiseUserService | undefined;
    protected disabled = true;

    constructor(request: IRequestContext) {
        const { relewiseApiKey, relewiseDatasetId, relewiseUrl, environmentName } = request.app.config ?? {};
        this.environmentName = environmentName;
        if (!relewiseApiKey || !relewiseDatasetId || !relewiseUrl || !this.environmentName) {
            console.error('[Relewise Search Service] Missing configuration.');
            return;
        }

        this.legalEntityId = request.channel?.InventLocationDataAreaId;
        if (!this.legalEntityId) {
            console.error('[Relewise Search Service] Legal Entity ID missing.');
            return;
        }

        this.locale = request.locale;
        if (!this.locale) {
            console.error('[Relewise Search Service] Locale missing.');
            return;
        }

        this.relewiseMappingService = new RelewiseMappingService(request);
        this.currencyId = this.relewiseMappingService.getCurrencyId();
        if (!this.currencyId) {
            console.error('[Relewise Search Service] Currency missing.');
            return;
        }

        this.relewiseUserService = new RelewiseUserService(request);
        this.searcher = new Searcher(relewiseDatasetId, relewiseApiKey, {
            serverUrl: relewiseUrl
        });

        this.disabled = false;
    }

    private isEnabled(): this is InitializedService {
        return !this.disabled;
    }

    public async autosuggestedSearch({
        query,
        shouldFetchProducts,
        shouldFetchCategories,
        shouldFetchSearchTermPredictions,
        numberOfProductResults,
        numberOfCategoryResults,
        numberOfKeywordResults
    }: AutosuggestSearchParams): Promise<AutosuggestSearchResult | undefined> {
        const shouldFetchSomething = shouldFetchProducts || shouldFetchCategories || shouldFetchSearchTermPredictions;
        if (!this.isEnabled() || !query || !shouldFetchSomething) {
            return;
        }

        try {
            const user = await this.relewiseUserService.getUser();
            const settings = {
                language: this.locale,
                currency: this.currencyId,
                displayedAtLocation: 'Header search',
                user
            };

            const batchedRequestBuilder = new SearchCollectionBuilder();

            if (shouldFetchProducts) {
                const productBuilder = new ProductSearchBuilder(settings)
                    .setTerm(query)
                    .pagination(p => p.setPageSize(numberOfProductResults))
                    .setExplodedVariants(NUMBER_OF_VARIANTS_PER_PRODUCT_IN_AUTOSUGGEST)
                    .setSelectedProductProperties({
                        displayName: true,
                        pricing: true,
                        allData: true
                    })
                    .setSelectedVariantProperties({
                        displayName: true,
                        pricing: true,
                        allSpecifications: true,
                        allData: true
                    })
                    .filters(f => {
                        f.addProductDataFilter(
                            'Environment',
                            b => b.addEqualsCondition({ value: this.environmentName, type: 'String', isCollection: false }),
                            true,
                            true
                        );
                        f.addProductDataFilter(
                            `HasVariantInStock_${this.legalEntityId}`,
                            b => b.addEqualsCondition({ value: true, type: 'Boolean', isCollection: false }),
                            true,
                            true
                        );
                        f.addVariantDataFilter(
                            `StockCount_${this.legalEntityId}`,
                            b => b.addGreaterThanCondition(RELEWISE_MINIMUM_STOCK_COUNT),
                            true,
                            true
                        );
                        f.addVariantDataFilter('UniqueColorId', b => b.addDistinctCondition(1), true, true);
                    });
                batchedRequestBuilder.addRequest(productBuilder.build());
            }

            if (shouldFetchCategories) {
                const categoryBuilder = new ProductCategorySearchBuilder(settings)
                    .setTerm(query)
                    .pagination(p => p.setPageSize(numberOfCategoryResults))
                    .setSelectedCategoryProperties({
                        displayName: true,
                        allData: true,
                        paths: true
                    })
                    .filters(f => {
                        f.addProductCategoryDataFilter(
                            'Environment',
                            b => b.addEqualsCondition({ value: this.environmentName, type: 'String', isCollection: false }),
                            true,
                            true
                        );
                        f.addProductCategoryDataFilter(
                            `ExistsInLegalEntity_${this.legalEntityId}`,
                            b => b.addEqualsCondition({ value: true, type: 'Boolean', isCollection: false }),
                            true,
                            true
                        );
                    });
                batchedRequestBuilder.addRequest(categoryBuilder.build());
            }

            if (shouldFetchSearchTermPredictions) {
                const searchTermPredictionBuilder = new SearchTermPredictionBuilder(settings).setTerm(query).take(numberOfKeywordResults);
                batchedRequestBuilder.addRequest(searchTermPredictionBuilder.build());
            }

            const batchResponse = await this.searcher.batch(batchedRequestBuilder.build());

            let searchTermPredictionResponse: SearchTermPredictionResponse | undefined;
            let categoryResponse: ProductCategorySearchResponse | undefined;
            let productResponse: ProductSearchResponse | undefined;
            if (shouldFetchSearchTermPredictions) {
                searchTermPredictionResponse = batchResponse?.responses?.pop() as SearchTermPredictionResponse;
            }
            if (shouldFetchCategories) {
                categoryResponse = batchResponse?.responses?.pop() as ProductCategorySearchResponse;
            }
            if (shouldFetchProducts) {
                productResponse = batchResponse?.responses?.pop() as ProductSearchResponse;
            }

            const products = productResponse?.results as RelewiseProductResult[] | undefined;
            const searchTermPredictions = searchTermPredictionResponse?.predictions as RelewiseSearchTermPredictionResult[] | undefined;
            const categories = categoryResponse?.results as RelewiseCategoryResult[] | undefined;

            return {
                products: products ?? undefined,
                categories: categories ?? undefined,
                searchTermPredictions: searchTermPredictions ?? undefined
            };
        } catch (err) {
            console.error('[Relewise Search Service] Search failed\n', err);
            return;
        }
    }
}
